/**
 * Watchtower's `Resource Group Tag` checks map one-to-one onto the Azure tag key
 * they read. The casing here is the casing we write, so it has to match what the
 * Watchtower pipeline looks up or a merge silently creates a second tag.
 */
const TAG_KEY_BY_CHECK: Record<string, string> = {
  tags_stage: 'stage',
  tags_application: 'application',
  tags_deployer: 'deployer',
};

export const getTagKeyForCheck = (checkName: string): string | null =>
  TAG_KEY_BY_CHECK[checkName] ?? null;

/**
 * Resource groups Azure creates and owns itself. Tagging them is usually either
 * rejected or reverted when the platform recreates them, so they start
 * deselected.
 */
const AZURE_MANAGED_PATTERNS = [
  /^MC_/i,
  /^DefaultResourceGroup/i,
  /^NetworkWatcherRG/i,
  /^AzureBackupRG_/i,
  /^databricks-rg-/i,
  /_managed$/i,
];

export const isAzureManagedResourceGroup = (name: string): boolean =>
  AZURE_MANAGED_PATTERNS.some(pattern => pattern.test(name));

/** Watchtower's placeholder for "this tag is not set". */
const MISSING_TAG_VALUE = 'no tag present';

const isRealTagValue = (value: string | undefined): value is string =>
  !!value?.trim() && value.trim().toLowerCase() !== MISSING_TAG_VALUE;

export const findExistingTagValue = (
  tags: Record<string, string>,
  tagKey: string,
): string | undefined => {
  const match = Object.entries(tags).find(
    ([key]) => key.toLowerCase() === tagKey.toLowerCase(),
  );
  const value = match?.[1];
  return isRealTagValue(value) ? value.trim() : undefined;
};

/**
 * Whether the tag is set exactly as we write it. Only then is the check sure to
 * pass on the next scorecard refresh; another casing may still need rewriting.
 */
export const hasExactTagValue = (
  tags: Record<string, string>,
  tagKey: string,
): boolean => isRealTagValue(tags[tagKey]);
